/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0
 * Implements RFC 7636 for enhanced security in OAuth flows
 */

/**
 * Generate a cryptographically secure random string for the code verifier
 * @returns Base64 URL-encoded random string
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64URLEncode(array)
}

/**
 * Generate the code challenge from the code verifier using SHA-256
 * @param verifier The code verifier to hash
 * @returns Base64 URL-encoded SHA-256 hash of the verifier
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64URLEncode(new Uint8Array(hash))
}

/**
 * Convert a Uint8Array to a Base64 URL-encoded string
 * @param buffer The buffer to encode
 * @returns Base64 URL-encoded string
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...Array.from(buffer)))
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Store the code verifier in session storage for later retrieval
 * @param verifier The code verifier to store
 */
export function storeCodeVerifier(verifier: string): void {
  sessionStorage.setItem('pkce_code_verifier', verifier)
}

/**
 * Retrieve the code verifier from session storage
 * @returns The stored code verifier or null if not found
 */
export function getCodeVerifier(): string | null {
  return sessionStorage.getItem('pkce_code_verifier')
}

/**
 * Remove the code verifier from session storage
 */
export function clearCodeVerifier(): void {
  sessionStorage.removeItem('pkce_code_verifier')
}
